import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as ReactRouter from 'react-router-dom';
import { loadingLinesFor, sectionLines } from './loadingLines';
import './BrandLoader.css';

/**
 * SAF3AI brand loader.
 *
 * Two marks, one component:
 *  - `shield` — a light beam sweeps down a shield while the brand "3" draws
 *    itself inside it. The default at every size, inline (`sm`) included: the
 *    loader is the 3-in-a-shield wherever it appears.
 *  - `three`  — the "3" drawing itself on its own, only when asked for by name.
 *    At `sm` it strokes in currentColor so it reads inside coloured buttons.
 *
 * Two scales, decided by where the loader sits (not by how wide its box is):
 *  - page    — loading the whole page: the xl shield with the module's quirky
 *              lines rotating underneath (loadingLines.js). A plain "Loading X…"
 *              the caller passed is kept for screen readers only.
 *  - section — loading one card, panel, table or drawer: the md shield with one
 *              short quirky line ("Crunching the numbers…"); a caller's specific
 *              message (anything not a generic "Loading…") is kept instead.
 *  `sm` stays inline (cells, buttons): a generic "Loading…" becomes the quirky
 *  line; a bare mark (no text) stays bare, a specific message stays as given.
 * `line` asks for the page scale explicitly (route fallbacks, full-page spinners).
 *
 * prefers-reduced-motion: the beam is hidden, the "3" is drawn in full and
 * still, and the line does not rotate.
 */

const SHIELD_PATH = 'M12 2 20 5v6c0 5.5-3.4 9.7-8 11-4.6-1.3-8-5.5-8-11V5z';
const SHIELD_THREE_PATH = 'M9.2 7.6h5l-2.6 3c1.9 0 3.1 1 3.1 2.6 0 1.7-1.3 2.8-3 2.8-1.1 0-2-.5-2.6-1.3';
const THREE_PATH = 'M7 4.5h9.5l-5 6.2c3.6 0 6 2 6 5 0 3.2-2.6 5.3-5.8 5.3-2.3 0-4.2-1-5.2-2.8';

export const BRAND_LOADER_SIZES = { sm: 16, md: 32, lg: 72, xl: 104 };

/**
 * `auto`: a loader in the app's main content that is NOT inside a card, panel,
 * surface, drawer, dialog or table is loading the page itself and becomes the page
 * loader. Width alone misjudged both ways (a section in a wide panel grew to page
 * size; a page in a narrow or not-yet-laid-out box stayed small). A box narrower
 * than this is never a page (side rails).
 */
export const PAGE_LOADER_MIN_WIDTH = 480;
export const SECTION_CONTAINERS = [
  '.card', '[class*="card"]', '[class*="panel"]', '[class*="surface"]', '[class*="drawer"]',
  '[class*="modal"]', '[class*="widget"]', '[class*="tile"]', '[role="dialog"]', 'table', '.aa-kpi',
].join(', ');
const GENERIC_MESSAGE = /^\s*loading\b/i;
const LINE_INTERVAL_MS = 2500;

/** Older callers pass `xs`; anything unknown falls back to md. */
const normaliseSize = (size) => {
  if (size === 'xs') return 'sm';
  return BRAND_LOADER_SIZES[size] ? size : 'md';
};

const prefersReducedMotion = () => {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/** useId() output contains characters (":" or "«»") that break url(#id) refs. */
const useSvgId = (suffix) => `bl${useId().replace(/[^a-zA-Z0-9_-]/g, '')}-${suffix}`;

const ShieldMark = ({ px }) => {
  const clipId = useSvgId('clip');
  const beamId = useSvgId('beam');
  return (
    <svg
      className="brand-loader__svg brand-loader__svg--shield"
      viewBox="0 0 24 24"
      width={px}
      height={px}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={clipId}>
          <path d={SHIELD_PATH} />
        </clipPath>
        <linearGradient id={beamId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9d8fff" stopOpacity="0" />
          <stop offset="0.55" stopColor="#9d8fff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#9d8fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path className="brand-loader__shield" d={SHIELD_PATH} />
      <g clipPath={`url(#${clipId})`}>
        <rect className="brand-loader__beam" x="0" y="0" width="24" height="5" fill={`url(#${beamId})`} />
      </g>
      <path className="brand-loader__three brand-loader__three--in-shield" d={SHIELD_THREE_PATH} pathLength="100" />
    </svg>
  );
};

const ThreeMark = ({ px, size }) => {
  const gradId = useSvgId('grad');
  const useGradient = size !== 'sm';
  return (
    <svg
      className="brand-loader__svg brand-loader__svg--three"
      viewBox="0 0 24 24"
      width={px}
      height={px}
      aria-hidden="true"
      focusable="false"
    >
      {useGradient && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#7c6cff" />
            <stop offset="1" stopColor="#589bff" />
          </linearGradient>
        </defs>
      )}
      <path
        className="brand-loader__three"
        d={THREE_PATH}
        pathLength="100"
        stroke={useGradient ? `url(#${gradId})` : 'currentColor'}
      />
    </svg>
  );
};

/** The rotating module line. Decorative: aria-hidden so screen readers are not re-announced every tick. */
const ModuleLine = ({ pathname }) => {
  const { t } = useTranslation('commonUi');
  const lines = loadingLinesFor(pathname);
  const [index, setIndex] = useState(0);
  const [still] = useState(prefersReducedMotion);

  useEffect(() => {
    setIndex(0);
  }, [pathname]);

  useEffect(() => {
    if (still || lines.length < 2) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % lines.length);
    }, LINE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [still, lines.length]);

  const current = lines[index % lines.length];
  return (
    <span
      key={current.key}
      className={`brand-loader__line${still || lines.length < 2 ? ' brand-loader__line--still' : ''}`}
      aria-hidden="true"
      data-testid="brand-loader-line"
    >
      {t(current.key, { defaultValue: current.defaultValue })}
    </span>
  );
};

/*
 * Read the router through the namespace, not named imports: three dozen suites
 * jest.mock('react-router-dom') with only the hooks their page uses, and a
 * missing named export would crash every Suspense fallback in them. Without
 * the hooks, the line falls back to window.location — same path under
 * BrowserRouter.
 */
const hasRouterHooks = typeof ReactRouter.useInRouterContext === 'function'
  && typeof ReactRouter.useLocation === 'function';
const useIsInRouter = hasRouterHooks ? ReactRouter.useInRouterContext : () => false;

const RouterModuleLine = () => {
  const location = ReactRouter.useLocation();
  return <ModuleLine pathname={location.pathname} />;
};

const WindowModuleLine = () => (
  <ModuleLine pathname={typeof window !== 'undefined' ? window.location.pathname : '/'} />
);

/** Picks the pathname source without calling useLocation outside a router (it throws there). */
const ModuleLineForCurrentRoute = () => {
  const inRouter = useIsInRouter();
  return inRouter ? <RouterModuleLine /> : <WindowModuleLine />;
};

/**
 * Is this loader filling a page? Measured before paint (no flash of the small one):
 * inside the app's main content, with page-wide room. Cards, table cells and
 * side panels are narrower and keep their size. jsdom measures 0, so tests that
 * don't ask for it never see the page loader.
 */
const usePageScale = (ref, enabled) => {
  const [page, setPage] = useState(false);
  useLayoutEffect(() => {
    if (!enabled || !ref.current) return;
    const el = ref.current;
    const host = el.parentElement;
    const main = el.closest('.main-content, .main-content-wrapper');
    if (!host || !main) return;
    const section = host.closest(SECTION_CONTAINERS);
    if (section && section !== main && main.contains(section)) return;
    // Page bodies centre the spinner in a flex box (`justify-content: center`), which
    // shrinks the spinner's own box to its content (~100px) and kept the Agent
    // Registry, agent and user loaders small. The wrapper is what fills the page.
    const box = main.contains(host.parentElement) ? host.parentElement : host;
    const width = Math.max(host.getBoundingClientRect().width, box.getBoundingClientRect().width);
    if (width >= PAGE_LOADER_MIN_WIDTH) setPage(true);
  }, [enabled, ref]);
  return page;
};

/** One short quirky line for a section loader, picked once per mount (no rotation). */
const SectionLine = () => {
  const { t } = useTranslation('commonUi');
  const [line] = useState(() => {
    const all = sectionLines();
    return all[Math.floor(Math.random() * all.length)];
  });
  return (
    <span className="brand-loader__line brand-loader__line--still brand-loader__line--section" data-testid="brand-loader-section-line">
      {t(line.key, { defaultValue: line.defaultValue })}
    </span>
  );
};

const BrandLoader = ({
  variant,
  size = 'md',
  message,
  defaultMessage,
  line = false,
  auto = false,
  bare = false,
  className = '',
}) => {
  const { t } = useTranslation('common');
  const rootRef = useRef(null);
  const asked = normaliseSize(size);
  const measuredPage = usePageScale(rootRef, auto && asked !== 'sm');
  const page = measuredPage || (line && asked !== 'sm');
  const s = page ? 'xl' : asked;
  const v = page ? 'shield' : (variant === 'three' ? 'three' : 'shield');
  const px = BRAND_LOADER_SIZES[s];
  const callerText = message || defaultMessage;
  // Page: the module's quirky lines are the visible text; the caller's message is for
  // screen readers. Section (md/lg): one quirky line, unless the caller said something
  // specific. Inline (sm): the caller's text as given.
  const showLine = page;
  // Inline (sm) loaders with no text at all (inside buttons) stay a bare mark; a generic
  // "Loading…" at any section size becomes a quirky line.
  const generic = Boolean(callerText) && GENERIC_MESSAGE.test(callerText);
  // `bare`: the caller shows its own text beside the mark (no line under it).
  const quirkySection = !bare && !page && (s === 'sm' ? generic : (!callerText || generic));
  const text = page ? null : (quirkySection ? null : callerText);
  const hiddenText = page ? message : (quirkySection ? callerText : null);

  const classes = [
    'brand-loader',
    `brand-loader--${s}`,
    `brand-loader--${v}`,
    page ? 'brand-loader--page' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} role="status" aria-live="polite" ref={rootRef}>
      {v === 'shield' ? <ShieldMark px={px} /> : <ThreeMark px={px} size={s} />}
      <span className="visually-hidden">{hiddenText || t('common:states.loading', { defaultValue: 'Loading…' })}</span>
      {(text || showLine || quirkySection) && (
        <div className="brand-loader__text">
          {text && <p className="brand-loader__message">{text}</p>}
          {showLine && <ModuleLineForCurrentRoute />}
          {quirkySection && <SectionLine />}
        </div>
      )}
    </div>
  );
};

export default BrandLoader;
